// Using Preact for smaller bundle size (Ensure alias/config is set up if needed)
import { h, Fragment, Component } from "preact";
import { useState, useEffect, useMemo, useCallback } from "preact/hooks";
import "./widget-styles.css"; // We'll create this for styling

// --- Interfaces (should align with backend/Prisma) ---
interface WidgetSettings {
  primaryColor: string;
  accentColor: string;
  fontFamily: string;
  buttonStyle: string;
  showLogo: boolean;
  logoUrl?: string | null;
  allowDeposits: boolean;
  depositAmount: number;
  maxPartySize: number;
  timeSlotInterval: number;
}

interface BusinessInfo {
  name: string;
  logoUrl?: string | null;
  // Add other needed fields like address, phone etc.
}

interface OpeningHour {
  dayOfWeek: number; // 0 = Sunday
  openTime: string; // HH:MM
  closeTime: string; // HH:MM
  isClosed: boolean;
}

interface BookingWidgetProps {
  businessId: string;
  apiBaseUrl?: string; // Allow overriding API endpoint
}

// --- Helper Function ---
function formatDateForApi(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
}

interface BookingWidgetState {
  settings: WidgetSettings | null;
  businessInfo: BusinessInfo | null;
  openingHours: OpeningHour[];
  isLoading: boolean;
  error: string | null;
  currentStep: number; // 1: Select Time, 2: Details, 3: Confirmation/Payment
  selectedDate: Date;
  selectedTime: string | null;
  partySize: number;
  availableTimes: string[];
  isLoadingTimes: boolean;
  // Form input states
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  specialRequests: string;
  // Submission states
  isSubmitting: boolean;
  submissionError: string | null;
  submissionSuccess: boolean;
}

export class BookingWidget extends Component<
  BookingWidgetProps,
  BookingWidgetState
> {
  constructor(props: BookingWidgetProps) {
    super(props);
    this.state = {
      settings: null,
      businessInfo: null,
      openingHours: [],
      isLoading: true,
      error: null,
      currentStep: 1,
      selectedDate: new Date(), // Default to today
      selectedTime: null,
      partySize: 2, // Default party size
      availableTimes: [],
      isLoadingTimes: false,
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      specialRequests: "",
      isSubmitting: false,
      submissionError: null,
      submissionSuccess: false,
    };
  }

  componentDidMount() {
    this.fetchInitialData();
  }

  componentDidUpdate(
    prevProps: BookingWidgetProps,
    prevState: BookingWidgetState
  ) {
    // Fetch availability when date or party size changes, but only after initial load
    if (
      !this.state.isLoading &&
      (prevState.selectedDate !== this.state.selectedDate ||
        prevState.partySize !== this.state.partySize)
    ) {
      this.fetchAvailability();
    }
  }

  fetchInitialData = async () => {
    const { businessId } = this.props;
    const apiBase = this.props.apiBaseUrl || "/api"; // Default or provided API base

    this.setState({ isLoading: true, error: null });

    try {
      // Fetch settings, business info, opening hours in parallel
      // IMPORTANT: These endpoints need to be PUBLICLY accessible
      const [settingsRes, businessRes, hoursRes] = await Promise.all([
        fetch(`${apiBase}/public/widget-settings/${businessId}`), // Placeholder URL
        fetch(`${apiBase}/public/businesses/${businessId}`), // Placeholder URL
        fetch(`${apiBase}/public/opening-hours/${businessId}`), // Placeholder URL
      ]);

      if (!settingsRes.ok || !businessRes.ok || !hoursRes.ok) {
        throw new Error("Failed to fetch initial widget data");
      }

      const settings = await settingsRes.json();
      const businessInfo = await businessRes.json();
      const openingHours = await hoursRes.json();

      this.setState(
        {
          settings,
          businessInfo,
          openingHours,
          isLoading: false,
          partySize: Math.min(
            this.state.partySize,
            settings?.maxPartySize || 10
          ), // Adjust default party size if needed
        },
        () => {
          this.fetchAvailability(); // Fetch initial availability for default date/party size
        }
      );

      // Apply dynamic styles
      this.applyStyles(settings);
    } catch (err) {
      this.setState({
        error: err instanceof Error ? err.message : "An unknown error occurred",
        isLoading: false,
      });
    }
  };

  applyStyles = (settings: WidgetSettings | null) => {
    if (!settings) return;
    const widgetElement = document.querySelector(".tablemint-widget"); // Target widget container
    const root = widgetElement || document.documentElement;
    root.style.setProperty("--widget-primary-color", settings.primaryColor);
    root.style.setProperty("--widget-accent-color", settings.accentColor);
    root.style.setProperty("--widget-font-family", settings.fontFamily);
    // Example for button style
    const borderRadius =
      settings.buttonStyle === "sharp"
        ? "0px"
        : settings.buttonStyle === "pill"
          ? "9999px"
          : "0.375rem";
    root.style.setProperty("--widget-border-radius", borderRadius);
  };

  fetchAvailability = async () => {
    const { businessId } = this.props;
    const { selectedDate, partySize } = this.state;
    if (!selectedDate || !partySize) return;

    const apiBase = this.props.apiBaseUrl || "/api";
    const dateString = formatDateForApi(selectedDate);

    this.setState({
      isLoadingTimes: true,
      availableTimes: [],
      selectedTime: null,
      error: null,
    });

    try {
      const response = await fetch(
        `${apiBase}/public/availability/${businessId}?date=${dateString}&partySize=${partySize}`
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message ||
            `Failed to fetch availability (${response.status})`
        );
      }
      const times: string[] = await response.json();
      this.setState({ availableTimes: times, isLoadingTimes: false });
    } catch (err) {
      this.setState({
        error:
          err instanceof Error
            ? err.message
            : "An unknown error occurred fetching times",
        isLoadingTimes: false,
      });
    }
  };

  // --- Event Handlers ---
  handleDateChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    this.setState({ selectedDate: new Date(target.value + "T00:00:00") }); // Ensure date is parsed correctly locally
  };

  handlePartySizeChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const size = parseInt(target.value, 10);
    const maxParty = this.state.settings?.maxPartySize || 10;
    if (!isNaN(size) && size > 0 && size <= maxParty) {
      this.setState({ partySize: size });
    }
  };

  handleTimeSelect = (time: string) => {
    this.setState({ selectedTime: time });
  };

  handleInputChange = (e: Event) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    const name = target.name;
    const value = target.value;
    // Use type assertion for state update
    this.setState({ [name]: value } as Pick<
      BookingWidgetState,
      keyof BookingWidgetState
    >);
  };

  goToNextStep = () => {
    const { currentStep, selectedTime } = this.state;
    if (currentStep === 1 && selectedTime) {
      this.setState({ currentStep: 2 });
    }
    // Step 2 validation could be added here before going to step 3
  };

  goToPrevStep = () => {
    if (this.state.currentStep > 1) {
      this.setState({ currentStep: this.state.currentStep - 1 });
    }
  };

  // --- Submission Logic ---
  handleSubmit = async (e: Event) => {
    e.preventDefault();
    const {
      selectedDate,
      selectedTime,
      partySize,
      customerName,
      customerEmail,
      customerPhone,
      specialRequests,
      settings,
    } = this.state;

    if (
      !selectedDate ||
      !selectedTime ||
      !partySize ||
      !customerName ||
      !customerEmail
    ) {
      this.setState({ submissionError: "Please fill in all required fields." });
      return;
    }

    this.setState({
      isSubmitting: true,
      submissionError: null,
      submissionSuccess: false,
    });
    const apiBase = this.props.apiBaseUrl || "/api";

    const payload = {
      businessId: this.props.businessId,
      date: formatDateForApi(selectedDate),
      time: selectedTime,
      partySize,
      customerName,
      customerEmail,
      customerPhone: customerPhone || undefined,
      specialRequests: specialRequests || undefined,
    };

    try {
      const response = await fetch(`${apiBase}/public/reservations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          throw new Error(
            responseData.message ||
              "Selected time slot is no longer available. Please go back and choose another time."
          );
        }
        throw new Error(
          responseData.message ||
            `Failed to create reservation (${response.status})`
        );
      }

      if (settings?.allowDeposits && responseData.checkoutUrl) {
        console.log(
          "Redirecting to Stripe Checkout:",
          responseData.checkoutUrl
        );
        window.location.href = responseData.checkoutUrl;
      } else {
        console.log(
          "Reservation submitted successfully (no deposit):",
          responseData
        );
        this.setState({ submissionSuccess: true, isSubmitting: false });
      }
    } catch (err) {
      console.error("Submission Error:", err);
      this.setState({
        submissionError:
          err instanceof Error
            ? err.message
            : "An unknown error occurred during submission",
        isSubmitting: false,
      });
    }
  };

  // --- Render Methods ---
  renderStep1 = () => {
    const {
      selectedDate,
      partySize,
      availableTimes,
      isLoadingTimes,
      error,
      selectedTime,
      settings,
    } = this.state;
    const maxParty = settings?.maxPartySize || 10;
    const todayString = formatDateForApi(new Date());
    return (
      <div class="widget-step">
        {/* Date Input */}
        <div class="widget-input-group">
          <label class="widget-label" for="widget-date">
            Date
          </label>
          <input
            type="date"
            id="widget-date"
            class="widget-input"
            value={formatDateForApi(selectedDate)}
            min={todayString}
            onChange={this.handleDateChange}
            disabled={this.state.isLoadingTimes} // Disable while loading times
          />
        </div>
        {/* Party Size Input */}
        <div class="widget-input-group">
          <label class="widget-label" for="widget-party-size">
            Party Size
          </label>
          <input
            type="number"
            id="widget-party-size"
            class="widget-input"
            value={partySize}
            min="1"
            max={maxParty}
            onChange={this.handlePartySizeChange}
            disabled={this.state.isLoadingTimes} // Disable while loading times
          />
        </div>

        <div class="widget-time-slots">
          <label class="widget-label">Available Times</label>
          {isLoadingTimes && (
            <div class="widget-loader--small">Loading times...</div>
          )}
          {!isLoadingTimes && availableTimes.length === 0 && !error && (
            <p class="widget-no-times">
              No available times found for this date and party size.
            </p>
          )}
          {!isLoadingTimes && error && (
            <p class="widget-error--small">{error}</p>
          )}
          <div class="widget-time-grid">
            {!isLoadingTimes &&
              availableTimes.map((time) => (
                <button
                  key={time}
                  type="button" // Ensure this is type button
                  class={`widget-time-button ${selectedTime === time ? "selected" : ""}`}
                  onClick={() => this.handleTimeSelect(time)}
                >
                  {time}
                </button>
              ))}
          </div>
        </div>

        <button
          type="button" // Ensure this is type button
          class="widget-button widget-button--next"
          onClick={this.goToNextStep}
          disabled={!selectedTime || isLoadingTimes}
        >
          Next
        </button>
      </div>
    );
  };

  renderStep2 = () => {
    const {
      customerName,
      customerEmail,
      customerPhone,
      specialRequests,
      isSubmitting,
      submissionError,
    } = this.state;
    return (
      <form class="widget-step widget-form" onSubmit={this.handleSubmit}>
        <h2 class="widget-step-title">Enter Your Details</h2>

        <div class="widget-input-group">
          <label class="widget-label" for="widget-name">
            Full Name *
          </label>
          <input
            type="text"
            id="widget-name"
            name="customerName" // Match state key
            class="widget-input"
            value={customerName}
            onChange={this.handleInputChange}
            required
            disabled={isSubmitting}
          />
        </div>

        <div class="widget-input-group">
          <label class="widget-label" for="widget-email">
            Email *
          </label>
          <input
            type="email"
            id="widget-email"
            name="customerEmail" // Match state key
            class="widget-input"
            value={customerEmail}
            onChange={this.handleInputChange}
            required
            disabled={isSubmitting}
          />
        </div>

        <div class="widget-input-group">
          <label class="widget-label" for="widget-phone">
            Phone (Optional)
          </label>
          <input
            type="tel"
            id="widget-phone"
            name="customerPhone" // Match state key
            class="widget-input"
            value={customerPhone}
            onChange={this.handleInputChange}
            disabled={isSubmitting}
          />
        </div>

        <div class="widget-input-group">
          <label class="widget-label" for="widget-requests">
            Special Requests (Optional)
          </label>
          <textarea
            id="widget-requests"
            name="specialRequests" // Match state key
            class="widget-input widget-textarea"
            value={specialRequests}
            onChange={this.handleInputChange}
            rows={3}
            disabled={isSubmitting}
          />
        </div>

        {submissionError && (
          <p class="widget-error--submit">{submissionError}</p>
        )}

        <div class="widget-button-group">
          <button
            type="button"
            class="widget-button widget-button--back"
            onClick={this.goToPrevStep}
            disabled={isSubmitting}
          >
            Back
          </button>
          <button
            type="submit"
            class="widget-button widget-button--submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit Reservation"}
          </button>
        </div>
      </form>
    );
  };

  renderStep3 = () => {
    const {
      submissionSuccess,
      businessInfo,
      selectedDate,
      selectedTime,
      partySize,
    } = this.state;
    if (!submissionSuccess) return null; // Should not be rendered otherwise

    return (
      <div class="widget-step widget-confirmation">
        <h2 class="widget-step-title">Reservation Request Sent!</h2>
        <p>
          Your request for {partySize} guest(s) on{" "}
          {selectedDate?.toLocaleDateString()} at {selectedTime} has been sent
          to {businessInfo?.name}.
        </p>
        <p>
          You should receive a confirmation email shortly if the reservation is
          accepted. Please check your inbox (and spam folder).
        </p>
        {/* TODO: Add a button to close or make another booking */}
      </div>
    );
  };

  render() {
    const {
      isLoading,
      error,
      settings,
      businessInfo,
      currentStep,
      submissionSuccess,
    } = this.state;

    if (isLoading) {
      return <div class="widget-loader">Loading...</div>;
    }

    // Handle initial load error more gracefully
    if (error && !settings) {
      // Check if settings failed to load
      return (
        <div class="widget-error">Error loading configuration: {error}</div>
      );
    }

    if (!settings || !businessInfo) {
      return <div class="widget-error">Configuration missing.</div>;
    }

    return (
      <div class="tablemint-widget">
        <div class="widget-header">
          {settings.showLogo && (
            <img
              src={
                settings.logoUrl ||
                businessInfo.logoUrl ||
                "./placeholder-logo.png"
              } // Added placeholder
              alt={`${businessInfo.name} Logo`}
              class="widget-logo"
              onError={(e) => (e.currentTarget.style.display = "none")} // Hide if logo fails
            />
          )}
          <h2>Book a Table at {businessInfo.name}</h2>
        </div>

        {/* Conditionally render steps or success message */}
        {submissionSuccess ? (
          this.renderStep3()
        ) : (
          <Fragment>
            {currentStep === 1 && this.renderStep1()}
            {currentStep === 2 && this.renderStep2()}
            {/* Step 3 is now only shown via submissionSuccess flag */}
          </Fragment>
        )}
      </div>
    );
  }
}
